import React from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import { Box } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { Select } from 'antd';
import {
  PATIENT_REQUEST_ASSIGNMENT_FILTER,
  REQUEST_ASSIGNMENT_IN_OFFICE,
  REQUEST_ASSIGNMENT_ORTHOSELECT,
  REQUEST_ASSIGNMENT_THIRD_PARTY,
  REQUEST_STEPS
} from '../../constants';
import RequestsStatusFilter from './RequestsStatusFilter';
import RequestsStepsFilter from './RequestsStepsFilter';
import { extractRequestTypeFromStatuses } from './helpers';

const PatientsFilter = ({
  handleChange,
  filterValues,
  withAssignmentFilter,
  fieldSizes,
  excludeRequests
}) => {
  const { Option } = Select;
  const selectedRequest = extractRequestTypeFromStatuses(filterValues.requestTypeStatus);
  const hasSteps = selectedRequest !== null && (REQUEST_STEPS[selectedRequest] || []).length > 0;
  const handleAssignmentsChange = value => {
    if (value.length) {
      handleChange(value, 'requestAssignments');
    } else {
      handleChange(
        [
          REQUEST_ASSIGNMENT_ORTHOSELECT,
          REQUEST_ASSIGNMENT_IN_OFFICE,
          REQUEST_ASSIGNMENT_THIRD_PARTY
        ],
        'requestAssignments'
      );
    }
  };

  const renderAssignmentsFilter = () => {
    const children = Object.keys(PATIENT_REQUEST_ASSIGNMENT_FILTER).map(key => {
      const { value, label } = PATIENT_REQUEST_ASSIGNMENT_FILTER[key];
      return (
        <Option key={label} value={value}>
          {label}
        </Option>
      );
    });
    return (
      <Select
        mode="multiple"
        placeholder="Please select"
        value={filterValues.requestAssignments}
        onChange={handleAssignmentsChange}
        style={{ width: '100%' }}
      >
        {children}
      </Select>
    );
  };

  const fields = (
    <>
      <RequestsStatusFilter
        value={filterValues.requestTypeStatus}
        onChange={value => handleChange(value, 'requestTypeStatus')}
        requestSize={fieldSizes?.request}
        statusSize={fieldSizes?.status}
        excludeRequests={excludeRequests}
      />
      {withAssignmentFilter && (
        <Grid item {...(fieldSizes?.assignment || { xs: 4 })}>
          <Typography component="span" variant="subtitle1">
            Assignment Filter
          </Typography>
          {renderAssignmentsFilter()}
        </Grid>
      )}

      {hasSteps && (
        <Grid item {...(fieldSizes?.steps || { xs: 12 })}>
          <RequestsStepsFilter
            onChange={value => {
              handleChange(value, 'requestStep');
            }}
            value={filterValues.requestStep}
            requestTypeStatusValue={filterValues.requestTypeStatus}
          />
        </Grid>
      )}
    </>
  );

  // sized fields flow into the surrounding grid; otherwise keep the standalone container
  if (fieldSizes) {
    return fields;
  }

  return (
    <Box mb={1}>
      <Grid container spacing={3}>
        {fields}
      </Grid>
    </Box>
  );
};

const gridSize = PropTypes.shape({
  xs: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  sm: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  md: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  lg: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  xl: PropTypes.oneOfType([PropTypes.number, PropTypes.bool])
});

PatientsFilter.propTypes = {
  handleChange: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  filterValues: PropTypes.object,
  withAssignmentFilter: PropTypes.bool,
  fieldSizes: PropTypes.shape({
    request: gridSize,
    status: gridSize,
    assignment: gridSize,
    steps: gridSize
  }),
  excludeRequests: PropTypes.arrayOf(PropTypes.string)
};

PatientsFilter.defaultProps = {
  filterValues: {
    requestTypeStatus: [],
    requestStep: null
  },
  withAssignmentFilter: true,
  fieldSizes: null,
  excludeRequests: []
};

export default PatientsFilter;
